/**
 * Error Handling Utilities
 * Centralized error handling for the application.
 */

import { logger } from './logger';
import { toast } from '@utils/toast';

/**
 * API Error class for structured error handling.
 */
export class ApiError extends Error {
  public readonly status: number;
  public readonly code?: string;
  public readonly data?: unknown;

  constructor(
    message: string,
    status: number,
    code?: string,
    data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.data = data;
  }

  /**
   * Creates an ApiError from an axios error response.
   */
  static fromResponse(error: unknown): ApiError {
    if (error instanceof ApiError) {
      return error;
    }

    // Handle axios-like error structure
    const axiosError = error as {
      response?: {
        status: number;
        data?: {
          message?: string;
          code?: string;
          errors?: unknown;
        };
      };
      message?: string;
    };

    if (axiosError.response) {
      return new ApiError(
        axiosError.response.data?.message || 'An error occurred',
        axiosError.response.status,
        axiosError.response.data?.code,
        axiosError.response.data?.errors
      );
    }

    return new ApiError(
      axiosError.message || 'An unexpected error occurred',
      500
    );
  }
}

/**
 * Validation Error class for form validation errors.
 */
export class ValidationError extends Error {
  public readonly errors: Record<string, string>;

  constructor(errors: Record<string, string>) {
    super('Validation failed');
    this.name = 'ValidationError';
    this.errors = errors;
  }
}

/**
 * Network Error class for connectivity issues.
 */
export class NetworkError extends Error {
  constructor(message = 'Network connection failed') {
    super(message);
    this.name = 'NetworkError';
  }
}

/**
 * Error messages by HTTP status code.
 */
const HTTP_ERROR_MESSAGES: Record<number, string> = {
  400: 'Invalid request. Please check your input.',
  401: 'Your session has expired. Please log in again.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested resource was not found.',
  408: 'Request timed out. Please try again.',
  409: 'This action conflicts with existing data.',
  422: 'The provided data is invalid.',
  429: 'Too many requests. Please wait and try again.',
  500: 'An internal server error occurred.',
  502: 'Service temporarily unavailable.',
  503: 'Service temporarily unavailable.',
  504: 'Request timed out. Please try again.',
};

/**
 * Gets a user-friendly error message for an HTTP status code.
 */
export const getHttpErrorMessage = (status: number): string => {
  return HTTP_ERROR_MESSAGES[status] || 'An unexpected error occurred';
};

/**
 * Error handler options.
 */
interface HandleErrorOptions {
  /** Context for logging */
  context?: string;
  /** Show toast notification */
  showToast?: boolean;
  /** Custom toast message */
  toastMessage?: string;
  /** Rethrow the error after handling */
  rethrow?: boolean;
  /** Callback for specific error types */
  onUnauthorized?: () => void;
  onForbidden?: () => void;
  onNotFound?: () => void;
  onValidation?: (errors: Record<string, string>) => void;
}

/**
 * Central error handler.
 */
export const handleError = (
  error: unknown,
  options: HandleErrorOptions = {}
): void => {
  const {
    context = 'Unknown',
    showToast = true,
    toastMessage,
    rethrow = false,
    onUnauthorized,
    onForbidden,
    onNotFound,
    onValidation,
  } = options;

  // Log the error
  logger.error(`Error in ${context}`, error);

  // Handle different error types
  if (error instanceof ApiError) {
    handleApiError(error, {
      showToast,
      toastMessage,
      onUnauthorized,
      onForbidden,
      onNotFound,
      onValidation,
    });
  } else if (error instanceof ValidationError) {
    if (onValidation) {
      onValidation(error.errors);
    }
    if (showToast) {
      toast.error(toastMessage || 'Please fix the validation errors');
    }
  } else if (error instanceof NetworkError) {
    if (showToast) {
      toast.error(toastMessage || 'Network connection failed. Please check your internet.');
    }
  } else if (error instanceof Error) {
    if (showToast) {
      toast.error(toastMessage || error.message || 'An unexpected error occurred');
    }
  } else {
    if (showToast) {
      toast.error(toastMessage || 'An unexpected error occurred');
    }
  }

  if (rethrow) {
    throw error;
  }
};

/**
 * Handles API errors specifically.
 */
const handleApiError = (
  error: ApiError,
  options: Omit<HandleErrorOptions, 'context' | 'rethrow'>
): void => {
  const {
    showToast,
    toastMessage,
    onUnauthorized,
    onForbidden,
    onNotFound,
    onValidation,
  } = options;

  // Handle specific status codes
  switch (error.status) {
    case 401:
      if (onUnauthorized) {
        onUnauthorized();
      }
      break;
    case 403:
      if (onForbidden) {
        onForbidden();
      }
      break;
    case 404:
      if (onNotFound) {
        onNotFound();
      }
      break;
    case 422:
      if (onValidation && error.data && typeof error.data === 'object') {
        onValidation(error.data as Record<string, string>);
      }
      break;
  }

  // Show toast notification
  if (showToast) {
    const message = toastMessage || error.message || getHttpErrorMessage(error.status);
    toast.error(message);
  }
};

/**
 * Wrapper for async functions with error handling.
 */
export const withErrorHandling = <T extends (...args: unknown[]) => Promise<unknown>>(
  fn: T,
  options: HandleErrorOptions = {}
) => {
  return async (...args: Parameters<T>): Promise<ReturnType<T> | undefined> => {
    try {
      return (await fn(...args)) as ReturnType<T>;
    } catch (error) {
      handleError(error, options);
      return undefined;
    }
  };
};

/**
 * Try-catch wrapper that returns a result tuple.
 */
export const tryCatch = async <T>(
  fn: () => Promise<T>
): Promise<[T, null] | [null, Error]> => {
  try {
    const result = await fn();
    return [result, null];
  } catch (error) {
    return [null, error instanceof Error ? error : new Error(String(error))];
  }
};

/**
 * Checks if an error is a network error.
 */
export const isNetworkError = (error: unknown): boolean => {
  if (error instanceof NetworkError) return true;
  
  const axiosError = error as {
    message?: string;
    code?: string;
  };
  
  return (
    axiosError.message === 'Network Error' ||
    axiosError.code === 'ECONNABORTED' ||
    axiosError.code === 'ERR_NETWORK'
  );
};

/**
 * Checks if an error is an authentication error.
 */
export const isAuthError = (error: unknown): boolean => {
  if (error instanceof ApiError) {
    return error.status === 401;
  }
  return false;
};

/**
 * Checks if an error is a validation error.
 */
export const isValidationError = (error: unknown): boolean => {
  if (error instanceof ValidationError) return true;
  if (error instanceof ApiError) {
    return error.status === 422;
  }
  return false;
};

